import { useState } from "react";
import { X, Loader2, Upload, Database } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Mono } from "./ui";

export default function ImportAssetsModal({ claimId, onClose, onImported }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [csvRows, setCsvRows] = useState(null);

  const pullT1 = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post(`/fieldclaim/integrations/t1/pull-assets?claim_id=${claimId}`);
      if (data.ok) { setMsg({ ok: true, text: `Imported ${data.created} assets from TechnologyOne.` }); setTimeout(onImported, 900); }
      else setMsg({ ok: false, text: data.reason === "not_configured" ? "TechnologyOne isn't connected yet. Configure it in FieldClaim → TechnologyOne, or use CSV import below." : `TechnologyOne error: ${data.message}` });
    } catch (e) { setMsg({ ok: false, text: formatApiErrorDetail(e?.response?.data?.detail) || "Pull failed." }); }
    finally { setBusy(false); }
  };

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];
    const head = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const idx = (names) => head.findIndex((h) => names.includes(h));
    const map = { name: idx(["name", "asset name", "asset"]), asset_type: idx(["asset_type", "type"]), road_id: idx(["road_id", "road id", "roadid"]), road_name: idx(["road_name", "road", "location"]), chainage_start: idx(["chainage_start", "chainage start", "ch start"]), chainage_end: idx(["chainage_end", "chainage finish", "ch end"]), latitude: idx(["latitude", "lat"]), longitude: idx(["longitude", "lng", "long"]) };
    return lines.slice(1).map((ln) => {
      const c = ln.split(",");
      const g = (k) => (map[k] >= 0 ? (c[map[k]] || "").trim() : "");
      return { name: g("name"), asset_type: g("asset_type") || "Other", road_id: g("road_id"), road_name: g("road_name"), chainage_start: g("chainage_start"), chainage_end: g("chainage_end"), latitude: g("latitude") ? Number(g("latitude")) : null, longitude: g("longitude") ? Number(g("longitude")) : null };
    }).filter((r) => r.name);
  };

  const onFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { const rows = parseCsv(String(reader.result)); setCsvRows(rows); setMsg(rows.length ? null : { ok: false, text: "No valid rows found. Ensure the CSV has a 'name' column." }); };
    reader.readAsText(f);
  };

  const onRecoverFile = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/fieldclaim/claims/${claimId}/import/recover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg({ ok: true, text: `Imported ${data.created} assets from Recover${data.skipped ? ` (${data.skipped} rows skipped)` : ""}.` });
      setTimeout(onImported, 1100);
    } catch (err) { setMsg({ ok: false, text: formatApiErrorDetail(err?.response?.data?.detail) || "Recover import failed." }); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const importCsv = async () => {
    if (!csvRows?.length) return;
    setBusy(true); setMsg(null);
    try { const { data } = await api.post(`/fieldclaim/claims/${claimId}/assets/import`, { rows: csvRows }); setMsg({ ok: true, text: `Imported ${data.created} assets.` }); setTimeout(onImported, 900); }
    catch (e) { setMsg({ ok: false, text: formatApiErrorDetail(e?.response?.data?.detail) || "Import failed." }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-navy-900 border border-gold-500/30 w-full max-w-md p-8 space-y-5" data-testid="import-assets-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl text-white">Import assets</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-white/60" /></button>
        </div>

        <div className="border border-gold-500/20 bg-navy-950/40 p-4 space-y-3">
          <div className="flex items-center gap-2"><Database className="h-4 w-4 text-gold-400" /><Mono className="text-[0.55rem] text-gold-400/80">TechnologyOne asset register</Mono></div>
          <p className="text-white/50 text-xs font-light">Pull roads, culverts and bridges directly from your council's OneCouncil EAM register.</p>
          <button onClick={pullT1} disabled={busy} data-testid="import-t1-btn" className="w-full border border-gold-500/40 text-gold-300 py-2.5 hover:bg-gold-500/10 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />} Pull from TechnologyOne</button>
        </div>

        <div className="border border-gold-500/20 bg-navy-950/40 p-4 space-y-3">
          <div className="flex items-center gap-2"><Upload className="h-4 w-4 text-gold-400" /><Mono className="text-[0.55rem] text-gold-400/80">Civica Recover export</Mono></div>
          <p className="text-white/50 text-xs font-light">Drop the .xlsx or .csv damage export straight from Asset Edge Recover. Columns are auto-mapped — crews keep their BAU capture.</p>
          <input type="file" accept=".xlsx,.xlsm,.csv" onChange={onRecoverFile} disabled={busy} data-testid="import-recover-input" className="block w-full text-xs text-white/60 file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-gold-500 file:text-navy-950 file:font-semibold file:cursor-pointer" />
        </div>

        <div className="border border-gold-500/20 bg-navy-950/40 p-4 space-y-3">
          <div className="flex items-center gap-2"><Upload className="h-4 w-4 text-gold-400" /><Mono className="text-[0.55rem] text-gold-400/80">CSV import (fallback)</Mono></div>
          <p className="text-white/50 text-xs font-light">Columns: name, asset_type, road_id, road_name, chainage_start, chainage_end, latitude, longitude.</p>
          <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="import-csv-input" className="block w-full text-xs text-white/60 file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-gold-500 file:text-navy-950 file:font-semibold file:cursor-pointer" />
          {csvRows && <div className="text-xs text-white/60" data-testid="csv-row-count">{csvRows.length} rows ready.</div>}
          {csvRows?.length > 0 && <button onClick={importCsv} disabled={busy} data-testid="import-csv-btn" className="w-full bg-gold-500 text-navy-950 font-semibold py-2.5 rounded-sm hover:bg-gold-400 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Import {csvRows.length} assets</button>}
        </div>

        {msg && <div className={`text-sm p-3 border ${msg.ok ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-amber-500/40 text-amber-300 bg-amber-500/10"}`} data-testid="import-result">{msg.text}</div>}
      </div>
    </div>
  );
}
